import type { PermissionDefinition } from '../types/permission.js';

// ─── System Permissions ─────────────────────────────────────────────
// Single source of truth for every permission in the system.
// Both frontend and backend import from here so permission keys are
// checked at compile time. The shape mirrors the Prisma Permission
// model (type + method + route) without DB-specific fields.
//
// Add new permissions here as they are introduced.

export const SystemPermissions = {
    // ── Identity Hub — Page ─────────────────────────────────────────
    IDENTITY_HUB_VIEW_PAGE: { type: 'PAGE', method: 'GET', route: 'identity-hub' },

    // ── Identity Hub — Users ────────────────────────────────────────
    IDENTITY_HUB_LIST_USERS: { type: 'ACTION', method: 'GET', route: '/users' },
    IDENTITY_HUB_LIST_ALL_USERS: { type: 'ACTION', method: 'GET', route: '/users/all' },
    IDENTITY_HUB_USER_STATS: { type: 'ACTION', method: 'GET', route: '/users/stats' },
    IDENTITY_HUB_GET_USER: { type: 'ACTION', method: 'GET', route: '/users/:userId' },
    IDENTITY_HUB_GET_USER_ACCESS: { type: 'ACTION', method: 'GET', route: '/users/:userId/access' },
    IDENTITY_HUB_LIST_USERS_ACCESS: { type: 'ACTION', method: 'GET', route: '/users/access/batch' },
    IDENTITY_HUB_CREATE_USER: { type: 'ACTION', method: 'POST', route: '/users/register' },
    IDENTITY_HUB_UPDATE_USER: { type: 'ACTION', method: 'PUT', route: '/users/update' },
    IDENTITY_HUB_DEACTIVATE_USER: { type: 'ACTION', method: 'DELETE', route: '/users/deactivate' },
    IDENTITY_HUB_REACTIVATE_USER: { type: 'ACTION', method: 'POST', route: '/users/reactivate' },
    IDENTITY_HUB_GRANT_ACCESS:   { type: 'ACTION', method: 'POST', route: '/users/access/grant' },
    IDENTITY_HUB_REVOKE_ACCESS:  { type: 'ACTION', method: 'POST', route: '/users/access/revoke' },
    IDENTITY_HUB_BULK_GRANT_ACCESS:  { type: 'ACTION', method: 'POST', route: '/users/access/bulk-grant' },
    IDENTITY_HUB_BULK_REVOKE_ACCESS: { type: 'ACTION', method: 'POST', route: '/users/access/bulk-revoke' },
    IDENTITY_HUB_REPLACE_ACCESS: { type: 'ACTION', method: 'POST', route: '/users/access/replace' },

    // ── Identity Hub — Tenants ──────────────────────────────────────
    IDENTITY_HUB_LIST_TENANTS: { type: 'ACTION', method: 'GET', route: '/tenants' },
    IDENTITY_HUB_GET_TENANT: { type: 'ACTION', method: 'GET', route: '/tenants/:tenantId' },
    IDENTITY_HUB_CREATE_TENANT: { type: 'ACTION', method: 'POST', route: '/tenants/provision' },
    IDENTITY_HUB_UPDATE_TENANT: { type: 'ACTION', method: 'PUT', route: '/tenants/update' },
    IDENTITY_HUB_DEACTIVATE_TENANT: { type: 'ACTION', method: 'DELETE', route: '/tenants/deactivate' },
    /** Same scope as DEACTIVATE — deactivating + reactivating an org is one logical privilege. */
    IDENTITY_HUB_REACTIVATE_TENANT: { type: 'ACTION', method: 'POST', route: '/tenants/reactivate' },
    IDENTITY_HUB_IMPORT_TENANT_SETTINGS: { type: 'ACTION', method: 'POST', route: '/tenants/import-settings' },
    // ── Identity Hub — Access Groups ────────────────────────────────
    IDENTITY_HUB_LIST_ACCESS: { type: 'ACTION', method: 'GET', route: '/access/by-tenant/:tenantId' },
    IDENTITY_HUB_LIST_ACCESS_BATCH: { type: 'ACTION', method: 'GET', route: '/access/by-tenant/batch' },
    IDENTITY_HUB_LIST_ALL_ACCESS: { type: 'ACTION', method: 'GET', route: '/access/all' },
    IDENTITY_HUB_COUNT_ACCESS: { type: 'ACTION', method: 'GET', route: '/access/count' },
    IDENTITY_HUB_GET_ACCESS: { type: 'ACTION', method: 'GET', route: '/access/:accessId' },
    IDENTITY_HUB_CREATE_ACCESS: { type: 'ACTION', method: 'POST', route: '/access/create' },
    IDENTITY_HUB_UPDATE_ACCESS: { type: 'ACTION', method: 'PUT', route: '/access/update' },
    IDENTITY_HUB_DELETE_ACCESS: { type: 'ACTION', method: 'DELETE', route: '/access/delete' },
    /** Same scope as DELETE — deleting + restoring a role is one logical privilege. */
    IDENTITY_HUB_RESTORE_ACCESS: { type: 'ACTION', method: 'POST', route: '/access/restore' },
    IDENTITY_HUB_UPDATE_PERMISSIONS: { type: 'ACTION', method: 'PUT', route: '/access/permissions' },

    // ── Identity Hub — Access Templates ──────────────────────────────
    IDENTITY_HUB_LIST_TEMPLATES:     { type: 'ACTION', method: 'GET',  route: '/templates' },
    IDENTITY_HUB_GET_TEMPLATE:       { type: 'ACTION', method: 'GET',  route: '/templates/:templateId' },
    IDENTITY_HUB_CREATE_TEMPLATE:    { type: 'ACTION', method: 'POST', route: '/templates/create' },
    IDENTITY_HUB_UPDATE_TEMPLATE:    { type: 'ACTION', method: 'PUT',  route: '/templates/update' },
    IDENTITY_HUB_ARCHIVE_TEMPLATE:   { type: 'ACTION', method: 'POST', route: '/templates/status' },
    IDENTITY_HUB_PREVIEW_TEMPLATE:   { type: 'ACTION', method: 'POST', route: '/templates/cascade-preview' },
    IDENTITY_HUB_GRANT_TEMPLATE:     { type: 'ACTION', method: 'POST', route: '/templates/grant' },
    IDENTITY_HUB_REVOKE_TEMPLATE:    { type: 'ACTION', method: 'POST', route: '/templates/revoke' },

    // ── Identity Hub — Invites ──────────────────────────────────────
    IDENTITY_HUB_LIST_INVITES:       { type: 'ACTION', method: 'GET',    route: '/invites' },
    IDENTITY_HUB_GENERATE_INVITE:    { type: 'ACTION', method: 'POST',   route: '/invites' },
    IDENTITY_HUB_BULK_INVITE:        { type: 'ACTION', method: 'POST',   route: '/invites/bulk' },
    IDENTITY_HUB_REGENERATE_INVITE:  { type: 'ACTION', method: 'POST',   route: '/invites/regenerate' },
    IDENTITY_HUB_RESEND_INVITE:      { type: 'ACTION', method: 'POST',   route: '/invites/:inviteId/resend' },
    IDENTITY_HUB_GET_INVITE_LINK:    { type: 'ACTION', method: 'GET',    route: '/invites/:inviteId/link' },
    IDENTITY_HUB_REVOKE_INVITE:      { type: 'ACTION', method: 'DELETE', route: '/invites/:inviteId' },

    // ── Identity Hub — Uploads ──────────────────────────────────────
    IDENTITY_HUB_UPLOAD_TENANT_LOGO_PRESIGN:      { type: 'ACTION', method: 'POST', route: '/uploads/presign' },
    IDENTITY_HUB_UPLOAD_TENANT_LOGO_CONFIRM:      { type: 'ACTION', method: 'POST', route: '/uploads/confirm' },
    IDENTITY_HUB_UPLOAD_TENANT_LOGO_STAGE_MEMORY: { type: 'ACTION', method: 'POST', route: '/uploads/stage-memory' },
    IDENTITY_HUB_UPLOAD_USER_AVATAR_PRESIGN:      { type: 'ACTION', method: 'POST', route: '/uploads/presign' },
    IDENTITY_HUB_UPLOAD_USER_AVATAR_CONFIRM:      { type: 'ACTION', method: 'POST', route: '/uploads/confirm' },
    IDENTITY_HUB_UPLOAD_USER_AVATAR_STAGE_MEMORY: { type: 'ACTION', method: 'POST', route: '/uploads/stage-memory' },

    // ── Identity Hub — Audit Logs ───────────────────────────────────
    IDENTITY_HUB_VIEW_AUDIT_LOG:        { type: 'ACTION', method: 'GET', route: '/audit-logs' },
    IDENTITY_HUB_VIEW_USER_AUDIT_LOG:   { type: 'ACTION', method: 'GET', route: '/audit-logs/users/:userId' },
    IDENTITY_HUB_VIEW_ACCESS_AUDIT_LOG: { type: 'ACTION', method: 'GET', route: '/audit-logs/access/:accessId' },
    IDENTITY_HUB_VIEW_TENANT_AUDIT_LOG: { type: 'ACTION', method: 'GET', route: '/audit-logs/tenants/:tenantId' },

    // ── Admin ───────────────────────────────────────────────────────
    ADMIN_VIEW_PAGE: { type: 'PAGE', method: 'GET', route: 'admin' },
    ADMIN_LIST_EVENT_JOBS: { type: 'ACTION', method: 'GET', route: '/admin/events' },

    // ── Admin — Changelog ───────────────────────────────────────────
    // Split deliberately: drafting release notes and deciding they go out to
    // every user, a Slack channel, and the team inbox are different levels of
    // trust, so the write keys and the approve/send keys are granted apart.
    ADMIN_LIST_CHANGELOG:            { type: 'ACTION', method: 'GET',    route: '/admin/changelog' },
    ADMIN_SYNC_CHANGELOG:            { type: 'ACTION', method: 'POST',   route: '/admin/changelog/sync' },
    ADMIN_REGENERATE_CHANGELOG:      { type: 'ACTION', method: 'POST',   route: '/admin/changelog/:versionTag/regenerate' },
    ADMIN_EDIT_CHANGELOG:            { type: 'ACTION', method: 'PUT',    route: '/admin/changelog/:versionTag' },
    ADMIN_APPROVE_CHANGELOG:         { type: 'ACTION', method: 'POST',   route: '/admin/changelog/:versionTag/approve' },
    ADMIN_REVOKE_CHANGELOG_APPROVAL: { type: 'ACTION', method: 'DELETE', route: '/admin/changelog/:versionTag/approval' },
    ADMIN_SEND_CHANGELOG:            { type: 'ACTION', method: 'POST',   route: '/admin/changelog/:versionTag/send' },
    ADMIN_LIST_CHANGELOG_RECIPIENTS: { type: 'ACTION', method: 'GET',    route: '/admin/changelog/recipients' },
    ADMIN_ADD_CHANGELOG_RECIPIENT:   { type: 'ACTION', method: 'POST',   route: '/admin/changelog/recipients' },
    // Keyed on the address rather than a row id — a team member who has never
    // been switched off has no row yet, and this request is what creates one.
    ADMIN_UPDATE_CHANGELOG_RECIPIENT:{ type: 'ACTION', method: 'PATCH',  route: '/admin/changelog/recipients' },
    ADMIN_REMOVE_CHANGELOG_RECIPIENT:{ type: 'ACTION', method: 'DELETE', route: '/admin/changelog/recipients/:recipientId' },
    // Reading how the changelog behaves is a listing-level grant; changing it
    // decides whether anything reaches Slack without a human, so it sits with
    // the approve and send keys rather than with the drafting ones.
    ADMIN_GET_CHANGELOG_SETTINGS:    { type: 'ACTION', method: 'GET',    route: '/admin/changelog/settings' },
    ADMIN_UPDATE_CHANGELOG_SETTINGS: { type: 'ACTION', method: 'PUT',    route: '/admin/changelog/settings' },
    // Posts a throwaway message so an admin can prove the webhook works
    // before trusting it with unattended posts. Sits with the settings keys:
    // it is a configuration check, not a release action.
    ADMIN_TEST_CHANGELOG_SLACK:      { type: 'ACTION', method: 'POST',   route: '/admin/changelog/slack-test' },
    // The same check against the other webhook. A separate key because the two
    // channels are separately configured, and proving one reaches Slack is not
    // permission to post into the other.
    ADMIN_TEST_ALERTS_SLACK:         { type: 'ACTION', method: 'POST',   route: '/admin/changelog/alerts-slack-test' },

    // ── Admin — Changelog digests ───────────────────────────────────
    // The same draft/approve split as entries. Digests are what actually reach
    // the mailing list and the in-app banner, so if anything the approve key
    // here is the more consequential of the two.
    ADMIN_LIST_CHANGELOG_DIGESTS:    { type: 'ACTION', method: 'GET',    route: '/admin/changelog/digests' },
    ADMIN_COMPOSE_CHANGELOG_DIGEST:  { type: 'ACTION', method: 'POST',   route: '/admin/changelog/digests' },
    ADMIN_LIST_DIGEST_CANDIDATES:    { type: 'ACTION', method: 'GET',    route: '/admin/changelog/digests/candidates' },
    ADMIN_EDIT_CHANGELOG_DIGEST:     { type: 'ACTION', method: 'PUT',    route: '/admin/changelog/digests/:digestId' },
    ADMIN_DELETE_CHANGELOG_DIGEST:   { type: 'ACTION', method: 'DELETE', route: '/admin/changelog/digests/:digestId' },
    ADMIN_REGENERATE_DIGEST:         { type: 'ACTION', method: 'POST',   route: '/admin/changelog/digests/:digestId/regenerate' },
    ADMIN_APPROVE_CHANGELOG_DIGEST:  { type: 'ACTION', method: 'POST',   route: '/admin/changelog/digests/:digestId/approve' },
    ADMIN_REVOKE_DIGEST_APPROVAL:    { type: 'ACTION', method: 'DELETE', route: '/admin/changelog/digests/:digestId/approval' },
    ADMIN_SEND_CHANGELOG_DIGEST:     { type: 'ACTION', method: 'POST',   route: '/admin/changelog/digests/:digestId/send' },
    ADMIN_PREVIEW_CHANGELOG_DIGEST_EMAIL: { type: 'ACTION', method: 'GET', route: '/admin/changelog/digests/:digestId/email-preview' },

    // ── Admin — Operations settings ─────────────────────────────────
    // How the Events and Scheduled jobs tabs behave: retention, defaults,
    // stuck thresholds, failure alerts. Reading is a listing-level grant;
    // writing decides whether event history is deleted and whether a Slack
    // channel gets paged, so it is a separate key.
    ADMIN_GET_OPS_SETTINGS:    { type: 'ACTION', method: 'GET', route: '/admin/settings/ops' },
    ADMIN_UPDATE_OPS_SETTINGS: { type: 'ACTION', method: 'PUT', route: '/admin/settings/ops' },

    // ── Dashboard ───────────────────────────────────────────────────
    DASHBOARD_VIEW_PAGE:   { type: 'PAGE',   method: 'GET', route: 'dashboard' },
    DASHBOARD_INDUSTRIES_LIST: { type: 'ACTION', method: 'GET', route: '/dashboard/industries' },
    DASHBOARD_INDUSTRY_PRODUCTS: { type: 'ACTION', method: 'GET', route: '/dashboard/industry-products' },
    // Write of tenant-wide competitor defaults. Page view does not include this.
    DASHBOARD_PUT_COMPETITOR_FILTER_DEFAULTS: { type: 'ACTION', method: 'PUT', route: '/dashboard/competitor-filter-defaults' },

    // ── Workspace ───────────────────────────────────────────────────
    WORKSPACE_VIEW_PAGE: { type: 'PAGE', method: 'GET', route: 'workspace' },

    // ── Personas (Persona Library) ──────────────────────────────────
    PERSONAS_VIEW_PAGE: { type: 'PAGE', method: 'GET', route: 'personas' },
    PERSONAS_LIST: { type: 'ACTION', method: 'GET', route: '/personas/list' },
    PERSONAS_GET: { type: 'ACTION', method: 'GET', route: '/personas/:id' },
    // Separate from PERSONAS_GET: usage names the opportunity sets a persona
    // feeds, which is persona-opportunities data surfaced on the Personas page.
    // Reading a persona's profile must not imply seeing the tenant's set names.
    PERSONAS_GET_USAGE: { type: 'ACTION', method: 'GET', route: '/personas/:id/usage' },
    PERSONAS_DISCOVER: { type: 'ACTION', method: 'POST', route: '/personas/discover' },
    PERSONAS_REMOVE: { type: 'ACTION', method: 'DELETE', route: '/personas/:id' },
    PERSONAS_REJECT: { type: 'ACTION', method: 'POST', route: '/personas/:id/reject' },
    PERSONAS_STATUS: { type: 'ACTION', method: 'GET', route: '/personas/status/:correlationId' },
    PERSONAS_ONBOARDING_STATUS: { type: 'ACTION', method: 'GET', route: '/personas/onboarding/status' },
    PERSONAS_ONBOARDING_SUGGESTIONS: { type: 'ACTION', method: 'GET', route: '/personas/onboarding/suggestions' },
    PERSONAS_ONBOARDING_ACCEPT: { type: 'ACTION', method: 'POST', route: '/personas/onboarding/accept' },
    PERSONAS_ONBOARDING_REJECT: { type: 'ACTION', method: 'POST', route: '/personas/onboarding/reject' },
    PERSONAS_ONBOARDING_UNDO: { type: 'ACTION', method: 'POST', route: '/personas/onboarding/undo' },
    PERSONAS_ONBOARDING_CONTINUE: { type: 'ACTION', method: 'POST', route: '/personas/onboarding/continue' },
    // Server-generated avatars (A2) reuse the shared upload routes; no client presign UI yet.
    PERSONAS_UPLOAD_AVATAR_PRESIGN:      { type: 'ACTION', method: 'POST', route: '/uploads/presign' },
    PERSONAS_UPLOAD_AVATAR_CONFIRM:      { type: 'ACTION', method: 'POST', route: '/uploads/confirm' },
    PERSONAS_UPLOAD_AVATAR_STAGE_MEMORY: { type: 'ACTION', method: 'POST', route: '/uploads/stage-memory' },

    // ── Marketplace ─────────────────────────────────────────────────
    MARKETPLACE_VIEW_PAGE: { type: 'PAGE', method: 'GET', route: 'marketplace' },
    MARKETPLACE_LIST_VENDORS: { type: 'ACTION', method: 'GET', route: '/marketplace/vendors' },
    MARKETPLACE_GET_VENDOR_DETAILS: { type: 'ACTION', method: 'GET', route: '/marketplace/vendors/:vendorId/questions' },
    MARKETPLACE_LIST_CONNECTIONS: { type: 'ACTION', method: 'GET', route: '/marketplace/connections' },
    MARKETPLACE_CREATE_CONNECTION: { type: 'ACTION', method: 'POST', route: '/marketplace/connections' },
    MARKETPLACE_UPDATE_CONNECTION_ANSWERS: { type: 'ACTION', method: 'PUT', route: '/marketplace/connections/:id/answers' },
    MARKETPLACE_RESIGN_CONNECTION: { type: 'ACTION', method: 'POST', route: '/marketplace/connections/:id/resign' },
    MARKETPLACE_DISCONNECT_VENDOR: { type: 'ACTION', method: 'DELETE', route: '/marketplace/connections/:id' },
    MARKETPLACE_LIST_ORDERS: { type: 'ACTION', method: 'GET', route: '/marketplace/orders' },
    MARKETPLACE_GET_ORDER: { type: 'ACTION', method: 'GET', route: '/marketplace/orders/:id' },
    MARKETPLACE_LIST_SENT_SCRIPTS: { type: 'ACTION', method: 'GET', route: '/marketplace/orders/sent-scripts' },
    MARKETPLACE_GET_ORDER_HANDOFF_DEFAULTS: { type: 'ACTION', method: 'GET', route: '/marketplace/order-handoff-defaults' },
    MARKETPLACE_PREVIEW_ORDER_HANDOFF_EMAIL: { type: 'ACTION', method: 'POST', route: '/marketplace/order-handoff-email-preview' },
    MARKETPLACE_CREATE_ORDER: { type: 'ACTION', method: 'POST', route: '/marketplace/orders' },
    MARKETPLACE_RETRY_ORDER: { type: 'ACTION', method: 'POST', route: '/marketplace/orders/:id/retry' },
    MARKETPLACE_UPDATE_ORDER_ETA: { type: 'ACTION', method: 'PUT', route: '/marketplace/orders/:id/eta' },
    MARKETPLACE_UPDATE_ORDER_FULFILLMENT: { type: 'ACTION', method: 'PATCH', route: '/marketplace/orders/:id/fulfillment-status' },
    MARKETPLACE_UPDATE_ORDER_SCRIPT_FULFILLMENT: { type: 'ACTION', method: 'PATCH', route: '/marketplace/orders/:id/scripts/:orderScriptId/fulfillment-status' },
    MARKETPLACE_DOWNLOAD_ORDER_SCRIPT_ASSET: { type: 'ACTION', method: 'GET', route: '/marketplace/orders/:id/scripts/:orderScriptId/download' },
    MARKETPLACE_LIST_ORDER_SCRIPT_PREFLIGHT_COMMENTS: { type: 'ACTION', method: 'GET', route: '/marketplace/orders/:id/scripts/:orderScriptId/preflight-comments' },
    MARKETPLACE_ADD_ORDER_SCRIPT_PREFLIGHT_COMMENT: { type: 'ACTION', method: 'POST', route: '/marketplace/orders/:id/scripts/:orderScriptId/preflight-comments' },
    MARKETPLACE_SET_ORDER_NOTE_REACTION: { type: 'ACTION', method: 'PUT', route: '/marketplace/orders/:id/scripts/:orderScriptId/preflight-comments/:commentId/reactions' },

    // ── Marketplace Ops Orders dashboard ─────────────────────────────
    MARKETPLACE_ORDERS_VIEW_PAGE: { type: 'PAGE', method: 'GET', route: 'marketplace.orders' },
    MARKETPLACE_ADMIN_LIST_ALL_ORDERS: { type: 'ACTION', method: 'GET', route: '/marketplace/admin/orders' },
    MARKETPLACE_ADMIN_UPDATE_ORDER_SCRIPT_FULFILLMENT: { type: 'ACTION', method: 'PATCH', route: '/marketplace/admin/orders/:id/scripts/:orderScriptId/fulfillment-status' },
    MARKETPLACE_ADMIN_UPDATE_ORDER_SCRIPT_VIDEO_LABEL: { type: 'ACTION', method: 'PATCH', route: '/marketplace/admin/orders/:id/scripts/:orderScriptId/videos/:deliverableId/label' },
    MARKETPLACE_ADMIN_ADD_ORDER_SCRIPT_VIDEO_SLOT: { type: 'ACTION', method: 'POST', route: '/marketplace/admin/orders/:id/scripts/:orderScriptId/video-slots' },
    MARKETPLACE_ADMIN_DELETE_ORDER_SCRIPT_VIDEO: { type: 'ACTION', method: 'DELETE', route: '/marketplace/admin/orders/:id/scripts/:orderScriptId/videos/:deliverableId' },
    MARKETPLACE_ADMIN_CREATE_ORDER_FOR_CLIENT: { type: 'ACTION', method: 'POST', route: '/marketplace/admin/orders' },
    MARKETPLACE_ADMIN_DUPLICATE_ORDER_SCRIPT: { type: 'ACTION', method: 'POST', route: '/marketplace/admin/orders/:id/scripts/:orderScriptId/duplicate-script' },
    MARKETPLACE_ADMIN_SEND_ORDER_SCRIPT_TO_PARTNER: { type: 'ACTION', method: 'POST', route: '/marketplace/admin/orders/:id/scripts/:orderScriptId/send-to-partner' },
    MARKETPLACE_ADMIN_ENABLE_PARTNER_LOGIN: { type: 'ACTION', method: 'POST', route: '/marketplace/admin/vendors/:vendorId/enable-partner-login' },
    MARKETPLACE_ADMIN_UNLINK_PARTNER_LOGIN: { type: 'ACTION', method: 'POST', route: '/marketplace/admin/vendors/:vendorId/unlink-partner-login' },

    // ── Marketplace Partner portal ───────────────────────────────────
    MARKETPLACE_PARTNER_VIEW_PAGE: { type: 'PAGE', method: 'GET', route: 'marketplace.partner' },
    MARKETPLACE_PARTNER_LIST_ORDERS: { type: 'ACTION', method: 'GET', route: '/marketplace/partner/orders' },
    MARKETPLACE_PARTNER_GET_ORDER: { type: 'ACTION', method: 'GET', route: '/marketplace/partner/orders/:id' },
    MARKETPLACE_PARTNER_UPLOAD_PREFLIGHT: { type: 'ACTION', method: 'POST', route: '/marketplace/partner/orders/:id/scripts/:orderScriptId/preflight' },
    MARKETPLACE_PARTNER_RETRY_PREFLIGHT_REVIEW: { type: 'ACTION', method: 'POST', route: '/marketplace/partner/orders/:id/scripts/:orderScriptId/preflight/retry-review' },
    MARKETPLACE_PARTNER_UPDATE_ORDER_SCRIPT_VIDEO_LABEL: { type: 'ACTION', method: 'PATCH', route: '/marketplace/partner/orders/:id/scripts/:orderScriptId/videos/:deliverableId/label' },
    MARKETPLACE_PARTNER_ADD_ORDER_SCRIPT_VIDEO_SLOT: { type: 'ACTION', method: 'POST', route: '/marketplace/partner/orders/:id/scripts/:orderScriptId/video-slots' },
    MARKETPLACE_PARTNER_DELETE_ORDER_SCRIPT_VIDEO_SLOT: { type: 'ACTION', method: 'DELETE', route: '/marketplace/partner/orders/:id/scripts/:orderScriptId/videos/:deliverableId' },
    MARKETPLACE_PARTNER_UPSERT_ORDER_SCRIPT_THUMBNAIL: { type: 'ACTION', method: 'PUT', route: '/marketplace/partner/orders/:id/scripts/:orderScriptId/thumbnails' },
    MARKETPLACE_PARTNER_DELETE_ORDER_SCRIPT_THUMBNAIL: { type: 'ACTION', method: 'DELETE', route: '/marketplace/partner/orders/:id/scripts/:orderScriptId/thumbnails' },
    MARKETPLACE_PARTNER_CHECK_VOICEOVER: { type: 'ACTION', method: 'POST', route: '/marketplace/partner/orders/:id/scripts/:orderScriptId/voiceover-check' },
    MARKETPLACE_PARTNER_PREFLIGHT_PRESIGN: { type: 'ACTION', method: 'POST', route: '/uploads/presign' },
    MARKETPLACE_PARTNER_PREFLIGHT_CONFIRM: { type: 'ACTION', method: 'POST', route: '/uploads/confirm' },
    MARKETPLACE_PARTNER_PREFLIGHT_STAGE_MEMORY: { type: 'ACTION', method: 'POST', route: '/uploads/stage-memory' },
    MARKETPLACE_PARTNER_DOWNLOAD_ORDER_SCRIPT_ASSET: { type: 'ACTION', method: 'GET', route: '/marketplace/partner/orders/:id/scripts/:orderScriptId/download' },
    MARKETPLACE_PARTNER_LIST_ORDER_SCRIPT_PREFLIGHT_COMMENTS: { type: 'ACTION', method: 'GET', route: '/marketplace/partner/orders/:id/scripts/:orderScriptId/preflight-comments' },
    MARKETPLACE_PARTNER_ADD_ORDER_SCRIPT_PREFLIGHT_COMMENT: { type: 'ACTION', method: 'POST', route: '/marketplace/partner/orders/:id/scripts/:orderScriptId/preflight-comments' },
    MARKETPLACE_PARTNER_SET_ORDER_NOTE_REACTION: { type: 'ACTION', method: 'PUT', route: '/marketplace/partner/orders/:id/scripts/:orderScriptId/preflight-comments/:commentId/reactions' },

    // ── Marketplace custom asset uploads (client org) ────────────────
    MARKETPLACE_CUSTOM_ASSET_PRESIGN: { type: 'ACTION', method: 'POST', route: '/uploads/presign' },
    MARKETPLACE_CUSTOM_ASSET_CONFIRM: { type: 'ACTION', method: 'POST', route: '/uploads/confirm' },
    MARKETPLACE_CUSTOM_ASSET_STAGE_MEMORY: { type: 'ACTION', method: 'POST', route: '/uploads/stage-memory' },
    MARKETPLACE_ADMIN_ADD_ORDER_SCRIPT_PREFLIGHT_COMMENT: { type: 'ACTION', method: 'POST', route: '/marketplace/admin/orders/:id/scripts/:orderScriptId/preflight-comments' },
    MARKETPLACE_ADMIN_LIST_ORDER_SCRIPT_PREFLIGHT_COMMENTS: { type: 'ACTION', method: 'GET', route: '/marketplace/admin/orders/:id/scripts/:orderScriptId/preflight-comments' },
    MARKETPLACE_ADMIN_VIEW_PAGE: { type: 'PAGE', method: 'GET', route: 'marketplace.admin' },
    MARKETPLACE_ADMIN_LIST_VENDORS: { type: 'ACTION', method: 'GET', route: '/marketplace/admin/vendors' },
    MARKETPLACE_ADMIN_GET_VENDOR: { type: 'ACTION', method: 'GET', route: '/marketplace/admin/vendors/:vendorId' },
    MARKETPLACE_ADMIN_CREATE_VENDOR: { type: 'ACTION', method: 'POST', route: '/marketplace/admin/vendors' },
    MARKETPLACE_ADMIN_REPLACE_VENDOR: { type: 'ACTION', method: 'PUT', route: '/marketplace/admin/vendors/:vendorId' },
    MARKETPLACE_ADMIN_ACTIVATE_VENDOR: { type: 'ACTION', method: 'POST', route: '/marketplace/admin/vendors/:vendorId/activate' },
    MARKETPLACE_ADMIN_DEACTIVATE_VENDOR: { type: 'ACTION', method: 'POST', route: '/marketplace/admin/vendors/:vendorId/deactivate' },
    // Approving an address makes it an egress target, so it is additionally
    // gated by requireSuperTenant on the route itself.
    MARKETPLACE_ADMIN_APPROVE_CONTACT_EMAIL: { type: 'ACTION', method: 'POST', route: '/marketplace/admin/vendors/:vendorId/contact-emails/:emailId/approve' },
    MARKETPLACE_ADMIN_REVOKE_CONTACT_EMAIL: { type: 'ACTION', method: 'POST', route: '/marketplace/admin/vendors/:vendorId/contact-emails/:emailId/revoke' },
    MARKETPLACE_ADMIN_LIST_TEAM_RECIPIENTS: { type: 'ACTION', method: 'GET', route: '/marketplace/admin/team-recipients' },
    MARKETPLACE_ADMIN_CREATE_TEAM_RECIPIENT: { type: 'ACTION', method: 'POST', route: '/marketplace/admin/team-recipients' },
    MARKETPLACE_ADMIN_UPDATE_TEAM_RECIPIENT: { type: 'ACTION', method: 'PUT', route: '/marketplace/admin/team-recipients/:recipientId' },
    MARKETPLACE_ADMIN_DELETE_TEAM_RECIPIENT: { type: 'ACTION', method: 'DELETE', route: '/marketplace/admin/team-recipients/:recipientId' },
    MARKETPLACE_ADMIN_GET_NOTIFICATION_SETTINGS: { type: 'ACTION', method: 'GET', route: '/marketplace/admin/notification-settings' },
    MARKETPLACE_ADMIN_UPDATE_NOTIFICATION_SETTINGS: { type: 'ACTION', method: 'PUT', route: '/marketplace/admin/notification-settings' },
    MARKETPLACE_ADMIN_LIST_PENDING_VENDORS: { type: 'ACTION', method: 'GET', route: '/marketplace/admin/vendors/pending' },
    MARKETPLACE_ADMIN_LIST_PENDING_HANDOFFS: { type: 'ACTION', method: 'GET', route: '/marketplace/admin/orders/pending-handoffs' },
    MARKETPLACE_ADMIN_RETRY_ORDER_HANDOFF: { type: 'ACTION', method: 'POST', route: '/marketplace/admin/orders/:id/retry-handoff' },
    MARKETPLACE_ADMIN_LINK_PENDING_VENDOR: { type: 'ACTION', method: 'POST', route: '/marketplace/admin/vendors/pending/:pendingId/link' },
    MARKETPLACE_ADMIN_REJECT_PENDING_VENDOR: { type: 'ACTION', method: 'POST', route: '/marketplace/admin/vendors/pending/:pendingId/reject' },
    MARKETPLACE_ADMIN_PARSE_ORDER_IMPORT: { type: 'ACTION', method: 'POST', route: '/marketplace/admin/orders/import/parse' },
    MARKETPLACE_ADMIN_GET_ORDER_IMPORT: { type: 'ACTION', method: 'GET', route: '/marketplace/admin/orders/import/:importId' },
    MARKETPLACE_ADMIN_UPDATE_ORDER_IMPORT_ROW: { type: 'ACTION', method: 'PATCH', route: '/marketplace/admin/orders/import/:importId/rows/:rowId' },
    MARKETPLACE_ADMIN_BULK_ORDER_IMPORT_START_DATES: { type: 'ACTION', method: 'PATCH', route: '/marketplace/admin/orders/import/:importId/client-start-dates' },
    MARKETPLACE_ADMIN_BULK_ORDER_IMPORT_VENDOR_MAPPING: { type: 'ACTION', method: 'PATCH', route: '/marketplace/admin/orders/import/:importId/vendor-mapping' },
    MARKETPLACE_ADMIN_BULK_ORDER_IMPORT_NEW_VENDOR_MAPPING: { type: 'ACTION', method: 'PATCH', route: '/marketplace/admin/orders/import/:importId/new-vendor-mapping' },
    MARKETPLACE_ADMIN_ACCEPT_ORDER_IMPORT_PREFILLED_DATES: { type: 'ACTION', method: 'POST', route: '/marketplace/admin/orders/import/:importId/accept-prefilled-dates' },
    MARKETPLACE_ADMIN_ACCEPT_ORDER_IMPORT_WARNINGS: { type: 'ACTION', method: 'POST', route: '/marketplace/admin/orders/import/:importId/accept-warnings' },
    MARKETPLACE_ADMIN_SEND_VENDOR_INVITE: { type: 'ACTION', method: 'POST', route: '/marketplace/admin/vendors/pending/:pendingId/invite' },
    MARKETPLACE_ADMIN_COMMIT_ORDER_IMPORT: { type: 'ACTION', method: 'POST', route: '/marketplace/admin/orders/import/:importId/commit' },
    MARKETPLACE_ADMIN_LIST_PENDING_IMPORT_ORDERS: { type: 'ACTION', method: 'GET', route: '/marketplace/admin/orders/pending-import' },
    MARKETPLACE_ADMIN_SEND_IMPORT_ROW_TO_PRODUCTION: { type: 'ACTION', method: 'POST', route: '/marketplace/admin/orders/import-rows/:rowId/send-to-production' },
    MARKETPLACE_ADMIN_UPLOAD_LOGO_PRESIGN: { type: 'ACTION', method: 'POST', route: '/uploads/presign' },
    MARKETPLACE_ADMIN_UPLOAD_LOGO_CONFIRM: { type: 'ACTION', method: 'POST', route: '/uploads/confirm' },
    MARKETPLACE_ADMIN_UPLOAD_LOGO_STAGE_MEMORY: { type: 'ACTION', method: 'POST', route: '/uploads/stage-memory' },

    // ── Overview ────────────────────────────────────────────────────
    OVERVIEW_VIEW_PAGE:   { type: 'PAGE',   method: 'GET', route: 'overview' },
    OVERVIEW_INDUSTRIES_LIST: { type: 'ACTION', method: 'GET', route: '/overview/industries' },
    OVERVIEW_INDUSTRY_PRODUCTS: { type: 'ACTION', method: 'GET', route: '/overview/industry-products' },
    OVERVIEW_PUT_COMPETITOR_FILTER_DEFAULTS: { type: 'ACTION', method: 'PUT', route: '/overview/competitor-filter-defaults' },
    OVERVIEW_SHARE_FILTER_PRESET: { type: 'ACTION', method: 'POST', route: '/overview/filter-presets/shared' },
    OVERVIEW_DELETE_SHARED_FILTER_PRESET: { type: 'ACTION', method: 'DELETE', route: '/overview/filter-presets/shared/:id' },
    OVERVIEW_REORDER_SHARED_FILTER_PRESETS: { type: 'ACTION', method: 'PUT', route: '/overview/filter-presets/shared/order' },

    // ── Drilldown ───────────────────────────────────────────────────
    DRILLDOWN_VIEW_PAGE: { type: 'PAGE',   method: 'GET', route: 'drilldown' },
    DRILLDOWN_INDUSTRIES_LIST: { type: 'ACTION', method: 'GET', route: '/drilldown/industries' },
    DRILLDOWN_INDUSTRY_PRODUCTS: { type: 'ACTION', method: 'GET', route: '/drilldown/industry-products' },
    DRILLDOWN_PUT_COMPETITOR_FILTER_DEFAULTS: { type: 'ACTION', method: 'PUT', route: '/drilldown/competitor-filter-defaults' },
    DRILLDOWN_SHARE_FILTER_PRESET: { type: 'ACTION', method: 'POST', route: '/drilldown/filter-presets/shared' },
    DRILLDOWN_DELETE_SHARED_FILTER_PRESET: { type: 'ACTION', method: 'DELETE', route: '/drilldown/filter-presets/shared/:id' },
    DRILLDOWN_REORDER_SHARED_FILTER_PRESETS: { type: 'ACTION', method: 'PUT', route: '/drilldown/filter-presets/shared/order' },

    // ── Opportunities ───────────────────────────────────────────────
    OPPORTUNITIES_VIEW_PAGE: { type: 'PAGE', method: 'GET', route: 'opportunities' },
    OPPORTUNITIES_LIST: { type: 'ACTION', method: 'GET', route: '/opportunities/list' },
    OPPORTUNITIES_FACETS: { type: 'ACTION', method: 'GET', route: '/opportunities/facets' },
    OPPORTUNITIES_CREATIVES: { type: 'ACTION', method: 'GET', route: '/opportunities/creatives' },
    OPPORTUNITIES_GENERATE_NEW_SET: { type: 'ACTION', method: 'POST', route: '/opportunities/generate-new-set' },
    OPPORTUNITIES_GENERATE: { type: 'ACTION', method: 'POST', route: '/opportunities/generate' },
    OPPORTUNITIES_REJECT: { type: 'ACTION', method: 'POST', route: '/opportunities/reject' },
    OPPORTUNITIES_EXPORT: { type: 'ACTION', method: 'POST', route: '/opportunities/export' },
    OPPORTUNITIES_STATUS: { type: 'ACTION', method: 'GET', route: '/opportunities/status/:correlationId' },
    OPPORTUNITIES_CANCEL: { type: 'ACTION', method: 'POST', route: '/opportunities/cancel' },
    OPPORTUNITIES_INDUSTRIES_LIST: { type: 'ACTION', method: 'GET', route: '/opportunities/industries' },
    OPPORTUNITIES_INDUSTRY_PRODUCTS: { type: 'ACTION', method: 'GET', route: '/opportunities/industry-products' },
    OPPORTUNITIES_TENANT_PRODUCTS: { type: 'ACTION', method: 'GET', route: '/opportunities/tenant-products' },
    OPPORTUNITIES_TENANT_PRODUCT_TYPES: { type: 'ACTION', method: 'GET', route: '/opportunities/tenant-product-types' },
    OPPORTUNITIES_COMPETITOR_OPTIONS: { type: 'ACTION', method: 'GET', route: '/opportunities/competitor-options' },

    // ── Opportunity Sets ──────────────────────────────────────────────
    OPPORTUNITY_SETS_LIST: { type: 'ACTION', method: 'GET', route: '/opportunity-sets/list' },
    OPPORTUNITY_SETS_CREATE: { type: 'ACTION', method: 'POST', route: '/opportunity-sets' },
    OPPORTUNITY_SETS_RENAME: { type: 'ACTION', method: 'PATCH', route: '/opportunity-sets/:id' },
    OPPORTUNITY_SETS_DELETE: { type: 'ACTION', method: 'DELETE', route: '/opportunity-sets/:id' },
    OPPORTUNITY_SETS_RETRY: { type: 'ACTION', method: 'POST', route: '/opportunity-sets/:id/retry' },
    OPPORTUNITY_SETS_MINIMAL: { type: 'ACTION', method: 'POST', route: '/opportunity-sets/minimal-generation' },

    // ── Opportunity Sets — Completed Scripts ──────────────────────────
    OPPORTUNITY_SETS_SCRIPTS_LIST:           { type: 'ACTION', method: 'GET',   route: '/opportunity-sets/:id/scripts' },
    OPPORTUNITY_SETS_SCRIPTS_FACETS:         { type: 'ACTION', method: 'GET',   route: '/opportunity-sets/:id/scripts/facets' },
    OPPORTUNITY_SETS_SCRIPTS_EXPORT:         { type: 'ACTION', method: 'GET',   route: '/opportunity-sets/:id/scripts/export.xlsx' },
    OPPORTUNITY_SETS_SCRIPTS_EXPORT_BY_IDS:  { type: 'ACTION', method: 'POST',  route: '/opportunity-sets/:id/scripts/export.xlsx' },
    OPPORTUNITY_SETS_SCRIPTS_REVERT:         { type: 'ACTION', method: 'PATCH', route: '/opportunity-sets/:id/scripts/:scriptId/revert' },
    OPPORTUNITY_SETS_SCRIPTS_FAVORITE:       { type: 'ACTION', method: 'PATCH', route: '/opportunity-sets/:id/scripts/:scriptId/favorite' },
    OPPORTUNITY_SETS_SCRIPTS_REORDER_SCENES: { type: 'ACTION', method: 'PATCH', route: '/opportunity-sets/:id/scripts/:scriptId/scenes/reorder' },
    OPPORTUNITY_SETS_SCRIPTS_AI_EDIT:        { type: 'ACTION', method: 'POST',  route: '/opportunity-sets/:id/scripts/ai-edit' },
    OPPORTUNITY_SETS_SCRIPTS_SUGGEST:        { type: 'ACTION', method: 'POST',  route: '/opportunity-sets/:id/scripts/suggest' },
    OPPORTUNITY_SETS_SCRIPTS_CUSTOM_BRIEF:   { type: 'ACTION', method: 'POST',  route: '/opportunity-sets/:id/scripts/custom-brief' },
    OPPORTUNITY_SETS_SCRIPTS_CUSTOM_ASSET_DOWNLOAD: {
        type: 'ACTION',
        method: 'GET',
        route: '/opportunity-sets/:id/scripts/:scriptId/custom-asset/download',
    },
    OPPORTUNITY_SETS_SCRIPTS_RESOLVE_LINKS:  { type: 'ACTION', method: 'POST',  route: '/opportunity-sets/:id/scripts/resolve-links' },
    OPPORTUNITIES_SCRIPT_STORYBOARD_GENERATE: { type: 'ACTION', method: 'POST', route: '/opportunity-sets/:id/scripts/:scriptId/storyboard' },

    // ── Opportunity Hub (PAGE slug persona-opportunities) ───
    PERSONA_OPPORTUNITIES_VIEW_PAGE: { type: 'PAGE', method: 'GET', route: 'persona-opportunities' },
    // Read bundle — list, detail, and Amber status. Replaces the former
    // PERSONA_OPPORTUNITY_SETS_USE bundle; same route, so existing grants keep read access.
    PERSONA_OPPORTUNITY_SETS_LIST: { type: 'ACTION', method: 'GET', route: '/persona-opportunity-sets' },
    PERSONA_OPPORTUNITY_SETS_GENERATE: { type: 'ACTION', method: 'POST', route: '/persona-opportunity-sets/generate' },
    // Capability permission (no mounted route). Checked in POST /generate when `advanced` is sent.
    PERSONA_OPPORTUNITY_SETS_QUERY_TUNING: {
        type: 'ACTION',
        method: 'POST',
        route: '/persona-opportunity-sets/generate/query-tuning',
    },
    PERSONA_OPPORTUNITY_SETS_REFERENCE_VIDEO_PRESIGN: {
        type: 'ACTION',
        method: 'POST',
        route: '/uploads/presign',
    },
    PERSONA_OPPORTUNITY_SETS_REFERENCE_VIDEO_CONFIRM: {
        type: 'ACTION',
        method: 'POST',
        route: '/uploads/confirm',
    },
    PERSONA_OPPORTUNITY_SETS_GUIDELINES_EXTRACT: {
        type: 'ACTION',
        method: 'POST',
        route: '/persona-opportunity-sets/:id/guidelines/extract',
    },
    PERSONA_OPPORTUNITY_SETS_SCRIPTS_CUSTOM_ASSET_DOWNLOAD: {
        type: 'ACTION',
        method: 'GET',
        route: '/persona-opportunity-sets/:id/scripts/:scriptId/custom-asset/download',
    },
    // Super-tenant only: adapt platform-tenant scripts into a new set on each chosen tenant.
    SCRIPTS_BROADCAST: { type: 'ACTION', method: 'POST', route: '/scripts-broadcast' },

    // ── Pre Flight ──────────────────────────────────────────────────────
    // Enforcement is per-route (`requirePermission` matches method + routeOptions.url
    // exactly, KTD8). The VIEW / GENERATE / MANAGE comment buckets are *grant bundles*
    // over these per-route keys — a role is granted a whole bucket, but every route
    // still carries its own key so a missing/ drifted key fails closed (Security #4).
    PREFLIGHT_VIEW_PAGE: { type: 'PAGE', method: 'GET', route: 'pre-flight' },

    // VIEW — read the catalogue, reports, and live status
    PREFLIGHT_GET_REPORT:    { type: 'ACTION', method: 'GET', route: '/preflight/reports/:id' },
    PREFLIGHT_LIST_REPORTS:  { type: 'ACTION', method: 'GET', route: '/preflight/reports' },
    PREFLIGHT_REPORT_STATUS: { type: 'ACTION', method: 'GET', route: '/preflight/reports/:id/status' },
    PREFLIGHT_GET_CRITERIA:  { type: 'ACTION', method: 'GET', route: '/preflight/criteria' },
    PREFLIGHT_EXPORT_PDF:    { type: 'ACTION', method: 'GET', route: '/preflight/reports/:id/export.pdf' },
    PREFLIGHT_LIST_MARKETS:  { type: 'ACTION', method: 'GET', route: '/preflight/markets' },
    PREFLIGHT_GET_INSIGHTS:  { type: 'ACTION', method: 'GET', route: '/preflight/insights' },
    PREFLIGHT_GET_CREATIVE_PREVIEWS: { type: 'ACTION', method: 'GET', route: '/preflight/creative-previews' },
    // Starring is a per-user preference, not a shared edit — it sits with VIEW so
    // anyone who can read the list can curate their own favorites.
    PREFLIGHT_FAVORITE_REPORT: { type: 'ACTION', method: 'PATCH', route: '/preflight/reports/:id/favorite' },

    // GENERATE — upload assets, create a report, re-run analysis
    PREFLIGHT_UPLOAD_PRESIGN:      { type: 'ACTION', method: 'POST', route: '/uploads/presign' },
    PREFLIGHT_UPLOAD_CONFIRM:      { type: 'ACTION', method: 'POST', route: '/uploads/confirm' },
    PREFLIGHT_UPLOAD_STAGE_MEMORY: { type: 'ACTION', method: 'POST', route: '/uploads/stage-memory' },
    PREFLIGHT_CREATE_REPORT:       { type: 'ACTION', method: 'POST', route: '/preflight/reports' },
    PREFLIGHT_RETRY_REPORT:        { type: 'ACTION', method: 'POST', route: '/preflight/reports/:id/retry' },

    // MANAGE — rename / reassign market / soft-delete any report in the tenant
    // (no ownership gate), and curate the tenant's controlled market and brand lists.
    // Reading the brand list is page-gated (`requirePagePermission`): everyone who can
    // open the page picks a brand in the New report form.
    // Rename and market reassignment share this PATCH route.
    PREFLIGHT_RENAME_REPORT: { type: 'ACTION', method: 'PATCH',  route: '/preflight/reports/:id' },
    PREFLIGHT_DELETE_REPORT: { type: 'ACTION', method: 'DELETE', route: '/preflight/reports/:id' },
    PREFLIGHT_CREATE_MARKET: { type: 'ACTION', method: 'POST',   route: '/preflight/markets' },
    PREFLIGHT_DELETE_MARKET: { type: 'ACTION', method: 'DELETE', route: '/preflight/markets/:id' },
    PREFLIGHT_CREATE_BRAND:  { type: 'ACTION', method: 'POST',   route: '/preflight/brands' },
    PREFLIGHT_UPDATE_BRAND:  { type: 'ACTION', method: 'PATCH',  route: '/preflight/brands/:id' },
    PREFLIGHT_DELETE_BRAND:  { type: 'ACTION', method: 'DELETE', route: '/preflight/brands/:id' },

    // ── Pre Flight – Scripts (script-review) ─────────────────────────
    // Standard grantable permissions — surfaced in the Identity Hub
    // permission-set editor and seeded into every org's Admins group.
    // `requirePermission` auto-passes SUPER_TENANT (Adge admins keep
    // cross-org access). The upload policy references the presign/confirm
    // keys. Distinct from PREFLIGHT (asset scoring).
    SCRIPT_REVIEW_VIEW_PAGE:           { type: 'PAGE',   method: 'GET',    route: 'pre-flight-scripts' },
    SCRIPT_REVIEW_UPLOAD_PRESIGN:      { type: 'ACTION', method: 'POST',   route: '/uploads/presign' },
    SCRIPT_REVIEW_UPLOAD_CONFIRM:      { type: 'ACTION', method: 'POST',   route: '/uploads/confirm' },
    SCRIPT_REVIEW_UPLOAD_STAGE_MEMORY: { type: 'ACTION', method: 'POST',   route: '/uploads/stage-memory' },
    SCRIPT_REVIEW_CREATE:              { type: 'ACTION', method: 'POST',   route: '/script-reviews' },
    SCRIPT_REVIEW_LIST:                { type: 'ACTION', method: 'GET',    route: '/script-reviews' },
    SCRIPT_REVIEW_GET:                 { type: 'ACTION', method: 'GET',    route: '/script-reviews/:id' },
    SCRIPT_REVIEW_GET_STATUS:          { type: 'ACTION', method: 'GET',    route: '/script-reviews/:id/status' },
    SCRIPT_REVIEW_POST_NOTE:           { type: 'ACTION', method: 'POST',   route: '/script-reviews/:id/notes' },
    SCRIPT_REVIEW_CREATE_SHARE:        { type: 'ACTION', method: 'POST',   route: '/script-reviews/:id/share' },
    SCRIPT_REVIEW_REVOKE_SHARE:        { type: 'ACTION', method: 'DELETE', route: '/script-reviews/:id/share' },

    // ── Post-Flight ──────────────────────────────────────────────────
    // Winning ads. The page and its list start ungranted — see
    // MANUAL_GRANT_ONLY_ROUTES. The list route itself arrives with the API.
    POST_FLIGHT_VIEW_PAGE: { type: 'PAGE', method: 'GET', route: 'post-flight' },
    POST_FLIGHT_LIST_OPPORTUNITIES: {
        type: 'ACTION',
        method: 'GET',
        route: '/post-flight/opportunities',
    },
    POST_FLIGHT_DOUBLE_DOWN: {
        type: 'ACTION',
        method: 'POST',
        route: '/post-flight/double-down',
    },

    // ── MCP (remote Model Context Protocol provider) ─────────────────
    // Master switch for services/mcp. Not a web page — the PAGE type is the
    // existing "capability, not route" shape, and MCP tools additionally
    // require their own ACTION key. Never auto-seeded (see
    // MANUAL_GRANT_ONLY_ROUTES in packages/db/src/seed/all-permissions.ts).
    MCP_ACCESS: { type: 'PAGE', method: 'GET', route: 'mcp' },
    // Per-tool ACTION keys. Tool calls arrive as JSON-RPC over POST /mcp, so
    // `/mcp/tools/<tool_name>` is a capability route with no Fastify mount —
    // the tool registry checks it programmatically. Every tool owns its key
    // here rather than borrowing the source page's, so the `mcp` domain stays
    // the single owner of what a chat client can reach.
    MCP_TOOL_GET_RECENT_OPPORTUNITIES: {
        type: 'ACTION',
        method: 'POST',
        route: '/mcp/tools/get_recent_opportunities',
    },
    MCP_TOOL_LIST_INSIGHT_SLICES: {
        type: 'ACTION',
        method: 'POST',
        route: '/mcp/tools/list_insight_slices',
    },
    MCP_TOOL_LIST_FILTER_OPTIONS: {
        type: 'ACTION',
        method: 'POST',
        route: '/mcp/tools/list_filter_options',
    },
    MCP_TOOL_GET_COMPETITIVE_INSIGHTS: {
        type: 'ACTION',
        method: 'POST',
        route: '/mcp/tools/get_competitive_insights',
    },
    MCP_TOOL_GET_BRAND_PERFORMANCE: {
        type: 'ACTION',
        method: 'POST',
        route: '/mcp/tools/get_brand_performance',
    },
    MCP_TOOL_GET_INDUSTRY_OVERVIEW: {
        type: 'ACTION',
        method: 'POST',
        route: '/mcp/tools/get_industry_overview',
    },
    MCP_TOOL_GET_BRAND_BRAIN: {
        type: 'ACTION',
        method: 'POST',
        route: '/mcp/tools/get_brand_brain',
    },
    MCP_TOOL_LIST_PERSONAS: {
        type: 'ACTION',
        method: 'POST',
        route: '/mcp/tools/list_personas',
    },
    MCP_TOOL_LIST_CREATIVE_EXAMPLES: {
        type: 'ACTION',
        method: 'POST',
        route: '/mcp/tools/nexus.list_creative_examples',
    },
    MCP_TOOL_SAVE_SCRIPT: {
        type: 'ACTION',
        method: 'POST',
        route: '/mcp/tools/save_script',
    },
    MCP_TOOL_SAVE_BRIEF: {
        type: 'ACTION',
        method: 'POST',
        route: '/mcp/tools/save_brief',
    },
    // Script management (AD-3559). One key per tool so a role can read scripts
    // without being able to change or delete them.
    MCP_TOOL_LIST_SCRIPTS: {
        type: 'ACTION',
        method: 'POST',
        route: '/mcp/tools/list_scripts',
    },
    MCP_TOOL_GET_SCRIPT: {
        type: 'ACTION',
        method: 'POST',
        route: '/mcp/tools/get_script',
    },
    MCP_TOOL_UPDATE_SCRIPT: {
        type: 'ACTION',
        method: 'POST',
        route: '/mcp/tools/update_script',
    },
    MCP_TOOL_DELETE_SCRIPT: {
        type: 'ACTION',
        method: 'POST',
        route: '/mcp/tools/delete_script',
    },
    MCP_TOOL_RESTORE_SCRIPT_VERSION: {
        type: 'ACTION',
        method: 'POST',
        route: '/mcp/tools/restore_script_version',
    },
    MCP_TOOL_RESTORE_DELETED_SCRIPT: {
        type: 'ACTION',
        method: 'POST',
        route: '/mcp/tools/restore_deleted_script',
    },
    // Atlas capability knowledge. One write grant and one read grant: the read
    // key is shared by list, exact read, and drift so Identity Hub stays one
    // checkbox for the three read tools.
    MCP_TOOL_CAPABILITIES_ADMIN_WRITE: {
        type: 'ACTION',
        method: 'POST',
        route: '/mcp/tools/write_feature_description',
    },
    MCP_TOOL_CAPABILITIES_ADMIN_READ: {
        type: 'ACTION',
        method: 'POST',
        route: '/mcp/tools/capabilities_admin_read',
    },
    // Customer reads of the same knowledge base. Separate keys so "what can I
    // do" and "what does Nexus offer" are granted one at a time.
    MCP_TOOL_GET_MY_CAPABILITIES: {
        type: 'ACTION',
        method: 'POST',
        route: '/mcp/tools/get_my_capabilities',
    },
    MCP_TOOL_GET_NEXUS_CAPABILITIES: {
        type: 'ACTION',
        method: 'POST',
        route: '/mcp/tools/get_nexus_capabilities',
    },

    // ── Public API (Pre Flight scoring on services/api) ────────────
    // Master switch for /public routes (and host-gated dedicated Public API DNS). Not a web page —
    // capability route like MCP. RBAC maps to existing PREFLIGHT_* ACTION keys.
    PUBLIC_API_ACCESS: { type: 'PAGE', method: 'GET', route: 'public-api' },
    PUBLIC_API_LIST_TOKENS: { type: 'ACTION', method: 'GET', route: '/api-tokens' },
    PUBLIC_API_CREATE_TOKEN: { type: 'ACTION', method: 'POST', route: '/api-tokens' },
    PUBLIC_API_UPDATE_TOKEN: { type: 'ACTION', method: 'PATCH', route: '/api-tokens/:id' },
    PUBLIC_API_DELETE_TOKEN: { type: 'ACTION', method: 'DELETE', route: '/api-tokens/:id' },

    // ── Prompts ──────────────────────────────────────────────────────
    PROMPTS_VIEW_PAGE: { type: 'PAGE', method: 'GET', route: 'prompts' },
    PROMPTS_LIST: { type: 'ACTION', method: 'GET', route: '/prompts' },
    PROMPTS_USAGE: { type: 'ACTION', method: 'GET', route: '/prompts/usage' },
    PROMPTS_GET: { type: 'ACTION', method: 'GET', route: '/prompts/:id' },
    PROMPTS_CREATE: { type: 'ACTION', method: 'POST', route: '/prompts' },
    PROMPTS_UPDATE: { type: 'ACTION', method: 'PATCH', route: '/prompts/:id' },
    PROMPTS_DELETE: { type: 'ACTION', method: 'DELETE', route: '/prompts/:id' },
    PROMPTS_CREATE_VERSION: { type: 'ACTION', method: 'POST', route: '/prompts/:id/versions' },
    PROMPTS_LIST_VERSIONS: { type: 'ACTION', method: 'GET', route: '/prompts/:id/versions' },
    PROMPTS_REVERT: { type: 'ACTION', method: 'POST', route: '/prompts/:id/revert/:versionId' },
    PROMPTS_RESOLVE: { type: 'ACTION', method: 'GET', route: '/prompts/by-used-in/:usedIn' },
    PROMPTS_USED_IN_VALUES: { type: 'ACTION', method: 'GET', route: '/prompts/used-in-values' },
    PROMPTS_LIST_VARIABLES: { type: 'ACTION', method: 'GET', route: '/prompts/variables/:usedIn' },
    PROMPTS_LIST_LLM_MODELS: { type: 'ACTION', method: 'GET', route: '/prompts/llm-models' },
    PROMPTS_REFRESH_LLM_MODELS: { type: 'ACTION', method: 'POST', route: '/prompts/llm-models/refresh' },
    PROMPTS_LIST_LABELS: { type: 'ACTION', method: 'GET', route: '/prompts/labels' },
    PROMPTS_VARIABLE_USAGES: { type: 'ACTION', method: 'GET', route: '/prompts/variables/:name/usages' },
    PROMPTS_BULK_REPLACE: { type: 'ACTION', method: 'POST', route: '/prompts/bulk-replace' },

    // ── Brand Brain ──────────────────────────────────────────────────
    // Update edits the visible onboarding fields through the same pipeline as
    // /tenants/:tenantId/onboarding; onboarding create stays in the Tenant flow.
    BRAND_BRAIN_VIEW_PAGE: { type: 'PAGE', method: 'GET', route: 'brand-brain' },
    BRAND_BRAIN_FETCH: { type: 'ACTION', method: 'POST', route: '/brand-brain/fetch' },
    BRAND_BRAIN_UPDATE: { type: 'ACTION', method: 'POST', route: '/brand-brain/update' },
    // ── Script delivery ──────────────────────────────────────────────
    SCRIPT_DELIVERY_VIEW_PAGE: { type: 'PAGE', method: 'GET', route: 'script-delivery' },
    SCRIPT_DELIVERY_GET_SUMMARY: { type: 'ACTION', method: 'GET', route: '/script-delivery/summary' },
    SCRIPT_DELIVERY_SET_TARGET: { type: 'ACTION', method: 'PUT', route: '/script-delivery/targets/:tenantId' },
    // ── Order status time (Adge admin dwell) ─────────────────────────
    ORDER_STATUS_TIME_VIEW_PAGE: { type: 'PAGE', method: 'GET', route: 'order-status-time' },
    ORDER_STATUS_TIME_LIST: { type: 'ACTION', method: 'GET', route: '/order-status-time' },

    // ── Beta Features ────────────────────────────────────────────────
    // Dark, per-feature PAGE/ACTION keys. Add them when a Beta Feature is
    // generated; the foundation ships none.
} as const satisfies Record<string, PermissionDefinition>;

export type SystemPermissionKey = keyof typeof SystemPermissions;

/**
 * Manual-grant ACTION routes that live in an inheriting page bundle. Holding
 * the page or a sibling action must not unlock them: Brand Brain update writes
 * customer onboarding data, so it reaches a role only through an explicit grant.
 */
export const BUNDLE_NON_INHERITED_ACTION_ROUTES = new Set<string>([
    SystemPermissions.BRAND_BRAIN_UPDATE.route,
]);

/**
 * ACTION routes a customer-org grant must not satisfy. Marketplace and prompts
 * routes are also seeded only onto SUPER_TENANT. Capability admin routes stay
 * manual-grant-only (they are not seeded onto Adge System Admins) and are
 * listed here so page-bundle inheritance and MCP `can()` treat them as
 * platform-only: a customer org must not read or write the global knowledge base.
 * MCP also skips its SUPER_TENANT bypass for these routes, so only a
 * super-tenant group that explicitly holds the route can use them.
 */
export const SUPER_TENANT_ONLY_ACTION_ROUTES = new Set<string>([
    SystemPermissions.MARKETPLACE_UPDATE_ORDER_ETA.route,
    SystemPermissions.MARKETPLACE_UPDATE_ORDER_FULFILLMENT.route,
    SystemPermissions.MARKETPLACE_UPDATE_ORDER_SCRIPT_FULFILLMENT.route,
    SystemPermissions.MARKETPLACE_ADMIN_LIST_ALL_ORDERS.route,
    SystemPermissions.MARKETPLACE_ADMIN_UPDATE_ORDER_SCRIPT_FULFILLMENT.route,
    SystemPermissions.MARKETPLACE_ADMIN_CREATE_ORDER_FOR_CLIENT.route,
    SystemPermissions.MARKETPLACE_ADMIN_DUPLICATE_ORDER_SCRIPT.route,
    SystemPermissions.MARKETPLACE_ADMIN_SEND_ORDER_SCRIPT_TO_PARTNER.route,
    SystemPermissions.MARKETPLACE_ADMIN_ADD_ORDER_SCRIPT_PREFLIGHT_COMMENT.route,
    SystemPermissions.MARKETPLACE_ADMIN_LIST_VENDORS.route,
    SystemPermissions.MARKETPLACE_ADMIN_GET_VENDOR.route,
    SystemPermissions.MARKETPLACE_ADMIN_CREATE_VENDOR.route,
    SystemPermissions.MARKETPLACE_ADMIN_REPLACE_VENDOR.route,
    SystemPermissions.MARKETPLACE_ADMIN_ACTIVATE_VENDOR.route,
    SystemPermissions.MARKETPLACE_ADMIN_DEACTIVATE_VENDOR.route,
    SystemPermissions.MARKETPLACE_ADMIN_LIST_TEAM_RECIPIENTS.route,
    SystemPermissions.MARKETPLACE_ADMIN_CREATE_TEAM_RECIPIENT.route,
    SystemPermissions.MARKETPLACE_ADMIN_UPDATE_TEAM_RECIPIENT.route,
    SystemPermissions.MARKETPLACE_ADMIN_DELETE_TEAM_RECIPIENT.route,
    SystemPermissions.MARKETPLACE_ADMIN_GET_NOTIFICATION_SETTINGS.route,
    SystemPermissions.MARKETPLACE_ADMIN_UPDATE_NOTIFICATION_SETTINGS.route,
    SystemPermissions.MARKETPLACE_ADMIN_ENABLE_PARTNER_LOGIN.route,
    SystemPermissions.MARKETPLACE_ADMIN_UNLINK_PARTNER_LOGIN.route,
    SystemPermissions.PROMPTS_LIST.route,
    SystemPermissions.PROMPTS_GET.route,
    SystemPermissions.PROMPTS_CREATE.route,
    SystemPermissions.PROMPTS_UPDATE.route,
    SystemPermissions.PROMPTS_DELETE.route,
    SystemPermissions.PROMPTS_CREATE_VERSION.route,
    SystemPermissions.PROMPTS_LIST_VERSIONS.route,
    SystemPermissions.PROMPTS_REVERT.route,
    SystemPermissions.PROMPTS_RESOLVE.route,
    SystemPermissions.PROMPTS_USED_IN_VALUES.route,
    SystemPermissions.PROMPTS_LIST_VARIABLES.route,
    SystemPermissions.PROMPTS_LIST_LLM_MODELS.route,
    SystemPermissions.PROMPTS_REFRESH_LLM_MODELS.route,
    SystemPermissions.PROMPTS_LIST_LABELS.route,
    SystemPermissions.PROMPTS_VARIABLE_USAGES.route,
    SystemPermissions.PROMPTS_BULK_REPLACE.route,
    SystemPermissions.SCRIPTS_BROADCAST.route,
    SystemPermissions.MCP_TOOL_CAPABILITIES_ADMIN_READ.route,
    SystemPermissions.MCP_TOOL_CAPABILITIES_ADMIN_WRITE.route,
    SystemPermissions.SCRIPT_DELIVERY_GET_SUMMARY.route,
    SystemPermissions.SCRIPT_DELIVERY_SET_TARGET.route,
    SystemPermissions.ORDER_STATUS_TIME_LIST.route,
]);

// Maps PAGE route → ACTION routes that belong to that page.
// Rule: if any ACTION route from a page's group is granted, the PAGE permission must also be granted.
export const PAGE_ACTION_DEPENDENCIES: Record<string, string[]> = {
    'identity-hub': [
        '/users', '/users/all', '/users/stats', '/users/:userId', '/users/:userId/access', '/users/access/batch', '/users/register',
        '/users/update', '/users/deactivate', '/users/reactivate',
        '/users/access/grant', '/users/access/revoke',
        '/users/access/bulk-grant', '/users/access/bulk-revoke', '/users/access/replace',
        '/tenants', '/tenants/:tenantId', '/tenants/provision', '/tenants/update', '/tenants/deactivate',
        '/tenants/reactivate', '/tenants/import-settings',
        '/access/by-tenant/:tenantId', '/access/by-tenant/batch', '/access/all', '/access/count', '/access/:accessId', '/access/create', '/access/update',
        '/access/delete', '/access/restore', '/access/permissions',
        '/templates', '/templates/:templateId', '/templates/create', '/templates/update', '/templates/status',
        '/templates/cascade-preview', '/templates/grant', '/templates/revoke',
        '/invites', '/invites/bulk', '/invites/regenerate',
        '/invites/:inviteId/resend', '/invites/:inviteId/link', '/invites/:inviteId',
        '/audit-logs', '/audit-logs/users/:userId', '/audit-logs/access/:accessId', '/audit-logs/tenants/:tenantId',
        '/uploads/presign', '/uploads/confirm', '/uploads/stage-memory',
    ],
    'admin': [
        '/admin/events',
        '/admin/changelog',
        '/admin/changelog/sync',
        '/admin/changelog/:versionTag/regenerate',
        '/admin/changelog/:versionTag',
        '/admin/changelog/:versionTag/approve',
        '/admin/changelog/:versionTag/approval',
        '/admin/changelog/:versionTag/send',
        '/admin/changelog/recipients',
        '/admin/changelog/recipients/:recipientId',
        '/admin/changelog/settings',
        '/admin/changelog/slack-test',
        '/admin/changelog/alerts-slack-test',
        '/admin/changelog/digests',
        '/admin/changelog/digests/candidates',
        '/admin/changelog/digests/:digestId',
        '/admin/changelog/digests/:digestId/regenerate',
        '/admin/changelog/digests/:digestId/approve',
        '/admin/changelog/digests/:digestId/approval',
        '/admin/changelog/digests/:digestId/send',
        '/admin/changelog/digests/:digestId/email-preview',
        '/admin/settings/ops',
    ],
    'dashboard': [
        '/dashboard/industries',
        '/dashboard/industry-products',
    ],
    'workspace': [],
    'personas': [
        '/personas/list',
        '/personas/:id',
        '/personas/:id/usage',
        '/personas/discover',
        '/personas/:id/reject',
        '/personas/status/:correlationId',
        '/personas/onboarding/status',
        '/personas/onboarding/suggestions',
        '/personas/onboarding/accept',
        '/personas/onboarding/reject',
        '/personas/onboarding/continue',
        '/personas/onboarding/undo',
    ],
    'overview': [
        '/overview/industries',
        '/overview/industry-products',
        '/overview/filter-presets/shared',
        '/overview/filter-presets/shared/:id',
        '/overview/filter-presets/shared/order',
    ],
    'drilldown': [
        '/drilldown/industries',
        '/drilldown/industry-products',
        '/drilldown/filter-presets/shared',
        '/drilldown/filter-presets/shared/:id',
        '/drilldown/filter-presets/shared/order',
    ],
    'marketplace': [
        '/marketplace/vendors',
        '/marketplace/vendors/:vendorId/questions',
        '/marketplace/connections',
        '/marketplace/connections/:id/answers',
        '/marketplace/connections/:id/resign',
        '/marketplace/connections/:id',
        '/marketplace/orders',
        '/marketplace/orders/:id',
        '/marketplace/orders/sent-scripts',
        '/marketplace/order-handoff-defaults',
        '/marketplace/order-handoff-email-preview',
        '/marketplace/orders/:id/retry',
        '/marketplace/orders/:id/scripts/:orderScriptId/download',
        '/marketplace/orders/:id/scripts/:orderScriptId/preflight-comments',
        '/marketplace/orders/:id/scripts/:orderScriptId/preflight-comments/:commentId/reactions',
        '/script-reviews/:id',
        '/script-reviews/:id/status',
    ],
    'marketplace.orders': [
        '/marketplace/admin/orders',
        '/marketplace/admin/orders/:id/scripts/:orderScriptId/fulfillment-status',
        '/marketplace/admin/orders/:id/scripts/:orderScriptId/videos/:deliverableId/label',
        '/marketplace/admin/orders/:id/scripts/:orderScriptId/video-slots',
        '/marketplace/admin/orders/:id/scripts/:orderScriptId/videos/:deliverableId',
        '/marketplace/admin/orders/:id/scripts/:orderScriptId/duplicate-script',
        '/marketplace/admin/orders/:id/scripts/:orderScriptId/send-to-partner',
        '/marketplace/orders/:id/eta',
        '/marketplace/orders/:id/fulfillment-status',
        '/marketplace/orders/:id/scripts/:orderScriptId/fulfillment-status',
        '/marketplace/orders/:id/scripts/:orderScriptId/download',
        '/script-reviews/:id',
        '/script-reviews/:id/status',
    ],
    'marketplace.partner': [
        '/marketplace/partner/orders',
        '/marketplace/partner/orders/:id',
        '/marketplace/partner/orders/:id/scripts/:orderScriptId/preflight',
        '/marketplace/partner/orders/:id/scripts/:orderScriptId/preflight/retry-review',
        '/marketplace/partner/orders/:id/scripts/:orderScriptId/videos/:deliverableId/label',
        '/marketplace/partner/orders/:id/scripts/:orderScriptId/video-slots',
        '/marketplace/partner/orders/:id/scripts/:orderScriptId/videos/:deliverableId',
        '/marketplace/partner/orders/:id/scripts/:orderScriptId/thumbnails',
        '/marketplace/partner/orders/:id/scripts/:orderScriptId/voiceover-check',
        '/marketplace/partner/orders/:id/scripts/:orderScriptId/download',
        '/marketplace/partner/orders/:id/scripts/:orderScriptId/preflight-comments',
        '/marketplace/partner/orders/:id/scripts/:orderScriptId/preflight-comments/:commentId/reactions',
        '/script-reviews/:id',
        '/script-reviews/:id/status',
        '/uploads/presign',
        '/uploads/confirm',
        '/uploads/stage-memory',
    ],
    'marketplace.admin': [
        '/marketplace/admin/vendors',
        '/marketplace/admin/vendors/:vendorId',
        '/marketplace/admin/vendors/:vendorId/activate',
        '/marketplace/admin/vendors/:vendorId/deactivate',
        '/marketplace/admin/vendors/:vendorId/contact-emails/:emailId/approve',
        '/marketplace/admin/vendors/:vendorId/contact-emails/:emailId/revoke',
        '/marketplace/admin/team-recipients',
        '/marketplace/admin/team-recipients/:recipientId',
        '/marketplace/admin/notification-settings',
        '/marketplace/admin/vendors/pending',
        '/marketplace/admin/orders/pending-handoffs',
        '/marketplace/admin/orders/:id/retry-handoff',
        '/marketplace/admin/vendors/pending/:pendingId/link',
        '/marketplace/admin/vendors/pending/:pendingId/reject',
        '/marketplace/admin/vendors/pending/:pendingId/invite',
        '/marketplace/admin/orders/import/parse',
        '/marketplace/admin/orders/import/:importId',
        '/marketplace/admin/orders/import/:importId/rows/:rowId',
        '/marketplace/admin/orders/import/:importId/client-start-dates',
        '/marketplace/admin/orders/import/:importId/vendor-mapping',
        '/marketplace/admin/orders/import/:importId/new-vendor-mapping',
        '/marketplace/admin/orders/import/:importId/accept-prefilled-dates',
        '/marketplace/admin/orders/import/:importId/accept-warnings',
        '/marketplace/admin/orders/import/:importId/commit',
        '/marketplace/admin/orders/pending-import',
        '/marketplace/admin/orders/import-rows/:rowId/send-to-production',
        '/marketplace/orders/:id/eta',
        '/marketplace/orders/:id/fulfillment-status',
        '/marketplace/orders/:id/scripts/:orderScriptId/download',
        '/marketplace/admin/orders',
        '/marketplace/admin/orders/:id/scripts/:orderScriptId/fulfillment-status',
        '/marketplace/admin/orders/:id/scripts/:orderScriptId/preflight-comments',
        '/marketplace/admin/vendors/:vendorId/enable-partner-login',
        '/marketplace/admin/vendors/:vendorId/unlink-partner-login',
        '/uploads/presign',
        '/uploads/confirm',
        '/uploads/stage-memory',
    ],
    'opportunities': [
        '/opportunities/list', '/opportunities/facets', '/opportunities/creatives',
        '/opportunities/generate-new-set', '/opportunities/generate', '/opportunities/reject',
        '/opportunities/export', '/opportunities/status/:correlationId', '/opportunities/cancel',
        '/opportunities/industries', '/opportunities/industry-products', '/opportunities/tenant-products',
        '/opportunities/tenant-product-types', '/opportunities/competitor-options',
        '/opportunity-sets/list', '/opportunity-sets', '/opportunity-sets/:id',
        '/opportunity-sets/:id/retry', '/opportunity-sets/minimal-generation',
        '/opportunity-sets/:id/scripts', '/opportunity-sets/:id/scripts/facets',
        '/opportunity-sets/:id/scripts/export.xlsx',
        '/opportunity-sets/:id/scripts/:scriptId/revert',
        '/opportunity-sets/:id/scripts/:scriptId/favorite',
        '/opportunity-sets/:id/scripts/:scriptId/scenes/reorder',
        '/opportunity-sets/:id/scripts/ai-edit',
        '/opportunity-sets/:id/scripts/suggest',
        '/opportunity-sets/:id/scripts/custom-brief',
        '/opportunity-sets/:id/scripts/:scriptId/custom-asset/download',
        '/uploads/presign',
        '/uploads/confirm',
        '/uploads/stage-memory',
        '/opportunity-sets/:id/scripts/resolve-links',
        '/opportunity-sets/:id/scripts/:scriptId/storyboard',
    ],
    'persona-opportunities': [
        '/persona-opportunity-sets',
        '/persona-opportunity-sets/generate',
        '/persona-opportunity-sets/user-generated',
        '/persona-opportunity-sets/generate/query-tuning',
        '/persona-opportunity-sets/:id',
        '/persona-opportunity-sets/:id/retry',
        '/persona-opportunity-sets/:id/regenerate',
        // Persona scripts — nested under sets; reuse GENERATE/LIST (no new ACTION keys).
        '/persona-opportunity-sets/:id/scripts/generate',
        '/persona-opportunity-sets/:id/scripts/delete',
        '/persona-opportunity-sets/:id/scripts/restore',
        '/persona-opportunity-sets/:id/scripts',
        '/persona-opportunity-sets/:id/opportunities/:opportunityId/scripts',
        '/persona-opportunity-sets/:id/briefs',
        '/persona-opportunity-sets/:id/opportunities/:opportunityId/briefs',
        '/persona-opportunity-sets/:id/briefs/:briefId/versions',
        '/persona-opportunity-sets/:id/briefs/:briefId/versions/:version/restore',
        '/persona-opportunity-sets/:id/briefs/:briefId/content',
        '/persona-opportunity-sets/:id/briefs/:briefId/favorite',
        '/persona-opportunity-sets/:id/briefs/ai-edit',
        '/persona-opportunity-sets/:id/briefs/delete',
        '/persona-opportunity-sets/:id/scripts/:scriptId/scene-versions',
        '/persona-opportunity-sets/:id/scripts/:scriptId/scene-versions/:version/restore',
        '/persona-opportunity-sets/:id/scripts/:scriptId/guidelines',
        '/persona-opportunity-sets/:id/scripts/:scriptId/content',
        '/persona-opportunity-sets/:id/scripts/:scriptId/favorite',
        '/persona-opportunity-sets/:id/scripts/:scriptId/hooks',
        '/persona-opportunity-sets/:id/scripts/:scriptId/hooks/suggest',
        '/persona-opportunity-sets/:id/scripts/:scriptId/hooks/confirm',
        '/persona-opportunity-sets/:id/scripts/ai-edit',
        '/persona-opportunity-sets/:id/scripts/suggest',
        '/persona-opportunity-sets/:id/scripts/:scriptId/custom-asset/download',
        '/persona-opportunity-sets/:id/scripts/resolve-links',
        '/persona-opportunity-sets/:id/scripts/:scriptId/storyboard',
        '/persona-opportunity-sets/:id/guidelines/extract',
        '/persona-opportunity-sets/:id/scripts/import',
        '/persona-opportunity-sets/:id/scripts/import-brief',
        '/persona-opportunity-sets/:id/scripts/import/extract',
        '/persona-opportunity-sets/:id/scripts/import/legacy',
        '/persona-opportunity-sets/legacy-opportunity-sets',
        '/persona-opportunity-sets/legacy-opportunity-sets/:legacySetId/scripts',
        '/uploads/presign',
        '/uploads/confirm',
        '/uploads/stage-memory',
    ],
    'pre-flight': [
        '/uploads/presign',
        '/uploads/confirm',
        '/uploads/stage-memory',
        '/preflight/reports',
        '/preflight/reports/:id',
        '/preflight/reports/:id/status',
        '/preflight/reports/:id/favorite',
        '/preflight/reports/:id/retry',
        '/preflight/reports/:id/export.pdf',
        '/preflight/criteria',
        '/preflight/markets',
        '/preflight/markets/:id',
        '/preflight/brands',
        '/preflight/brands/:id',
        '/preflight/insights',
        '/preflight/creative-previews',
    ],
    'pre-flight-scripts': [
        '/uploads/presign',
        '/uploads/confirm',
        '/uploads/stage-memory',
        '/script-reviews',
        '/script-reviews/:id',
        '/script-reviews/:id/status',
        '/script-reviews/:id/notes',
        '/script-reviews/:id/share',
    ],
    'post-flight': [
        '/post-flight/opportunities',
        '/post-flight/double-down',
    ],
    // Every MCP tool is listed here — MCP_ACCESS is the master switch, and the
    // tool's own key is what grants the capability. A tool missing from this
    // bundle can be granted without MCP_ACCESS, which fails closed but is
    // invisible in the role editor.
    'mcp': [
        '/mcp/tools/get_recent_opportunities',
        '/mcp/tools/list_insight_slices',
        '/mcp/tools/list_filter_options',
        '/mcp/tools/get_competitive_insights',
        '/mcp/tools/get_brand_performance',
        '/mcp/tools/get_industry_overview',
        '/mcp/tools/get_brand_brain',
        '/mcp/tools/list_personas',
        '/mcp/tools/nexus.list_creative_examples',
        '/mcp/tools/save_script',
        '/mcp/tools/save_brief',
        '/mcp/tools/list_scripts',
        '/mcp/tools/get_script',
        '/mcp/tools/update_script',
        '/mcp/tools/delete_script',
        '/mcp/tools/restore_script_version',
        '/mcp/tools/restore_deleted_script',
        '/mcp/tools/write_feature_description',
        '/mcp/tools/capabilities_admin_read',
        '/mcp/tools/get_my_capabilities',
        '/mcp/tools/get_nexus_capabilities',
    ],
    'public-api': [
        '/api-tokens',
        '/api-tokens/:id',
        '/preflight/markets',
        '/uploads/presign',
        '/uploads/confirm',
        '/preflight/reports',
        '/preflight/reports/:id/status',
        '/preflight/reports/:id',
    ],
    'prompts': [
        '/prompts',
        '/prompts/usage',
        '/prompts/:id',
        '/prompts/:id/versions',
        '/prompts/:id/revert/:versionId',
        '/prompts/by-used-in/:usedIn',
        '/prompts/used-in-values',
        '/prompts/variables/:usedIn',
        '/prompts/llm-models',
        '/prompts/llm-models/refresh',
        '/prompts/labels',
        '/prompts/variables/:name/usages',
        '/prompts/bulk-replace',
    ],
    'brand-brain': ['/brand-brain/fetch', '/brand-brain/update'],
    'script-delivery': [
        '/script-delivery/summary',
        '/script-delivery/targets/:tenantId',
        '/order-status-time/dashboard',
    ],
    'order-status-time': [
        '/order-status-time',
        '/order-status-time/dashboard',
    ],
};
